
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation, useStaggerAnimation } from '../../hooks/useScrollAnimation';
import SEO from '../../components/SEO';
import EngagementBar from '../../components/feature/EngagementBar';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [showContactSuccess, setShowContactSuccess] = useState(false);
  const [showNewsletterSuccess, setShowNewsletterSuccess] = useState(false);
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [serviceAreasOpen, setServiceAreasOpen] = useState(false);
  const [mobileServiceAreasOpen, setMobileServiceAreasOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    setTimeout(() => setHeroLoaded(true), 100);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      setShowBackToTop(window.scrollY > 600);

      const sections = ['home', 'services', 'products', 'app-integration', 'live-monitoring', 'contact'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (sectionId: string) => {
    setMobileMenuOpen(false);
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  const scrollToContact = () => {
    scrollToSection('contact');
  };

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const message = formData.get('message') as string;
    if (message && message.length > 500) {
      alert('Message must be 500 characters or less');
      return;
    }

    const services = Array.from(form.querySelectorAll('input[name="services"]:checked'))
      .map((input: any) => input.value)
      .join(', ');

    const data = new URLSearchParams();
    data.append('first_name', formData.get('first_name') as string);
    data.append('last_name', formData.get('last_name') as string);
    data.append('email', formData.get('email') as string);
    data.append('phone', formData.get('phone') as string);
    data.append('property_type', formData.get('property_type') as string);
    data.append('services', services);
    data.append('message', message);

    try {
      const response = await fetch('https://readdy.ai/api/form/d6937vtbd17lru29l3m0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: data.toString(),
      });

      if (response.ok) {
        setShowContactSuccess(true);
        form.reset();
        setTimeout(() => setShowContactSuccess(false), 5000);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const data = new URLSearchParams();
    data.append('email', formData.get('email') as string);

    try {
      const response = await fetch('https://readdy.ai/api/form/d6937vtbd17lru29l3mg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: data.toString(),
      });

      if (response.ok) {
        setShowNewsletterSuccess(true);
        form.reset();
        setTimeout(() => setShowNewsletterSuccess(false), 5000);
      }
    } catch (error) {
      console.error('Newsletter submission error:', error);
    }
  };

  // Scroll animation hooks for each section
  const servicesHeader = useScrollAnimation();
  const servicesCards = useStaggerAnimation(4);
  const productsHeader = useScrollAnimation();
  const productsCards = useStaggerAnimation(3);
  const partnersSection = useScrollAnimation();
  const aboutLeft = useScrollAnimation({ rootMargin: '0px 0px -40px 0px' });
  const aboutRight = useScrollAnimation({ rootMargin: '0px 0px -40px 0px' });
  const aboutStats = useStaggerAnimation(4);
  const appText = useScrollAnimation({ rootMargin: '0px 0px -40px 0px' });
  const appImage = useScrollAnimation({ rootMargin: '0px 0px -40px 0px' });
  const monitoringHeader = useScrollAnimation();
  const monitoringCards = useStaggerAnimation(3);
  const monitoringCta = useScrollAnimation();
  const testimonialSection = useScrollAnimation();
  const reviewsHeader = useScrollAnimation();
  const reviewsCards = useStaggerAnimation(6);
  const reviewsStats = useScrollAnimation();
  const clientsHeader = useScrollAnimation();
  const clientsGrid = useScrollAnimation();
  const contactSection = useScrollAnimation();
  const serviceAreasHeader = useScrollAnimation();
  const serviceAreasGrid = useScrollAnimation();
  const serviceAreasCta = useScrollAnimation();
  const footerSection = useScrollAnimation({ threshold: 0.05 });

  const serviceAreas = [
    { name: 'Toronto', path: '/service-areas/toronto' },
    { name: 'Mississauga', path: '/service-areas/mississauga' },
    { name: 'Brampton', path: '/service-areas/brampton' },
    { name: 'Oakville', path: '/service-areas/oakville' },
    { name: 'Hamilton', path: '/service-areas/hamilton' },
    { name: 'Richmond Hill', path: '/service-areas/richmond-hill' },
    { name: 'Vaughan', path: '/service-areas/vaughan' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Home Security Systems & Commercial Security Solutions | Alliance Security Systems"
        description="Trusted security systems provider since 1999. Professional alarm systems, HD security cameras, access control & 24/7 monitoring across the Greater Toronto Area. 30,000+ installations. Call 1-888-458-9181 for a free quote."
        keywords="security systems Toronto, alarm systems GTA, security cameras Mississauga, access control systems, 24/7 monitoring, home security, commercial security, Alliance Security"
        canonical="/"
      />

      {/* Navigation */}
      <nav className="bg-white shadow-md sticky top-0 z-50" aria-label="Main navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="flex flex-col leading-none border-2 border-[#D4A853] rounded-lg px-4 py-2.5 bg-[#0A1628]">
              <span className="text-2xl sm:text-3xl font-extrabold tracking-wider text-[#D4A853]" style={{ fontFamily: "'Inter', sans-serif" }}>ALLIANCE</span>
              <span className="text-[10px] sm:text-xs font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
            </div>
          </Link>
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden w-10 h-10 flex items-center justify-center text-gray-700 cursor-pointer">
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
          <div className="hidden md:flex items-center gap-6">
            <Link to="/camera-packages" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">Cameras</Link>
            <Link to="/access-control" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">Access Control</Link>
            <Link to="/alarm-packages" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">Alarm Packages</Link>
            <Link to="/live-monitoring" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">Live Monitoring</Link>
            <Link to="/business-security" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer whitespace-nowrap">Business Security</Link>
            <Link to="/clients" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">Clients</Link>
            <a href="#about" onClick={(e) => { e.preventDefault(); scrollToSection('about'); }} className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer whitespace-nowrap">About Us</a>
            <Link to="/faq" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">FAQ</Link>
            <Link to="/blog" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">Blog</Link>
            <Link to="/contact" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">Contact Us</Link>
            <div 
              className="relative"
              onMouseEnter={() => setServiceAreasOpen(true)}
              onMouseLeave={() => setServiceAreasOpen(false)}
            >
              <button className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap">
                Service Areas
                <i className={`ri-arrow-down-s-line text-base transition-transform ${serviceAreasOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {serviceAreasOpen && (
                <div className="absolute top-full right-0 mt-2 w-52 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50">
                  {serviceAreas.map((area) => (
                    <Link
                      key={area.path}
                      to={area.path}
                      className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-[#D4A853]/10 hover:text-[#D4A853] transition-colors cursor-pointer"
                    >
                      {area.name}
                    </Link>
                  ))}
                  <div className="border-t border-gray-100 mt-1 pt-1">
                    <Link
                      to="/service-areas"
                      className="block px-4 py-2.5 text-sm font-semibold text-[#D4A853] hover:bg-[#D4A853]/10 transition-colors cursor-pointer"
                    >
                      View All Areas →
                    </Link>
                  </div>
                </div>
              )}
            </div>
            <button onClick={scrollToContact} className="bg-[#D4A853] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap">Get Free Quote</button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-4 pb-4 space-y-2">
            <Link to="/camera-packages" className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">Camera Packages</Link>
            <Link to="/access-control" className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">Access Control</Link>
            <Link to="/alarm-packages" className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">Alarm Packages</Link>
            <Link to="/live-monitoring" className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">Live Monitoring</Link>
            <Link to="/business-security" className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">Business Security</Link>
            <Link to="/clients" className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">Clients</Link>
            <a href="#about" onClick={(e) => { e.preventDefault(); scrollToSection('about'); }} className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">About Us</a>
            <Link to="/faq" className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">FAQ</Link>
            <Link to="/blog" className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">Blog</Link>
            <Link to="/contact" className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">Contact Us</Link>
            <div className="border-b border-gray-50">
              <button 
                onClick={() => setMobileServiceAreasOpen(!mobileServiceAreasOpen)}
                className="w-full flex items-center justify-between py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] cursor-pointer"
              >
                Service Areas
                <i className={`ri-arrow-down-s-line text-base transition-transform ${mobileServiceAreasOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {mobileServiceAreasOpen && (
                <div className="pl-4 pb-3 space-y-1">
                  {serviceAreas.map((area) => (
                    <Link
                      key={area.path}
                      to={area.path}
                      className="block py-2 text-sm text-gray-600 hover:text-[#D4A853] transition-colors cursor-pointer"
                    >
                      {area.name}
                    </Link>
                  ))}
                  <Link
                    to="/service-areas"
                    className="block py-2 text-sm font-semibold text-[#D4A853] hover:text-[#B8923D] transition-colors cursor-pointer"
                  >
                    View All Areas →
                  </Link>
                </div>
              )}
            </div>
            <button onClick={scrollToContact} className="w-full bg-[#D4A853] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap mt-2">Get Free Quote</button>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center pt-20 sm:pt-0">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/9729d3924b1f859a56d55df752d8318a.jpg" 
            alt="Security Control Room" 
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 w-full py-12 sm:py-0">
          <div className="max-w-3xl">
            <h1 
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-white mb-4 sm:mb-6 leading-tight"
              style={{
                opacity: heroLoaded ? 1 : 0,
                transform: heroLoaded ? 'translateY(0)' : 'translateY(40px)',
                transition: 'opacity 0.8s ease, transform 0.8s ease',
              }}
            >
              Protecting What Matters Most for 25 Years
            </h1>
            <p 
              className="text-lg sm:text-xl md:text-2xl text-white/90 mb-6 sm:mb-8 tracking-wide"
              style={{
                opacity: heroLoaded ? 1 : 0,
                transform: heroLoaded ? 'translateY(0)' : 'translateY(30px)',
                transition: 'opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s',
              }}
            >
              30,000+ Installations | Professional Security Solutions Since 1999
            </p>
            <div 
              className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4"
              style={{
                opacity: heroLoaded ? 1 : 0,
                transform: heroLoaded ? 'translateY(0)' : 'translateY(30px)',
                transition: 'opacity 0.8s ease 0.4s, transform 0.8s ease 0.4s',
              }}
            >
              <button 
                onClick={() => scrollToSection('contact')}
                className="bg-[#D4A853] text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-[#B8923D] transition-all flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl"
              >
                Get Free Security Assessment
                <span className="w-7 h-7 sm:w-8 sm:h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <i className="ri-arrow-right-line text-base sm:text-lg"></i>
                </span>
              </button>
              <a 
                href="tel:18884589181"
                className="border-2 border-white text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-white hover:text-gray-900 transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-phone-line text-base sm:text-lg"></i>
                Call Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <main>
        <section id="services" className="py-12 sm:py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div 
              ref={servicesHeader.ref}
              className="text-center mb-10 sm:mb-16"
              style={{
                opacity: servicesHeader.isVisible ? 1 : 0,
                transform: servicesHeader.isVisible ? 'translateY(0)' : 'translateY(40px)',
                transition: 'opacity 0.7s ease, transform 0.7s ease',
              }}
            >
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-6 py-2 rounded-full text-sm font-semibold mb-4 whitespace-nowrap">
                OUR EXPERTISE
              </span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#0A1628] mb-4">
                Comprehensive Security Solutions
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
                Trusted by thousands of businesses and homeowners for over two decades
              </p>
            </div>

            <div ref={servicesCards.ref} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
              {[
                {
                  icon: 'ri-shield-check-line',
                  title: 'Smart Alarm Systems',
                  description: 'Advanced alarm systems with mobile app control, instant alerts, and seamless integration with your smart home.',
                  bg: 'bg-[#0A1628]/5',
                  color: 'text-[#0A1628]',
                  link: '/alarm-packages'
                },
                {
                  icon: 'ri-camera-line',
                  title: 'HD Security Cameras',
                  description: 'Crystal-clear video surveillance with live monitoring, night vision, and cloud storage for complete peace of mind.',
                  bg: 'bg-[#D4A853]/10',
                  color: 'text-[#D4A853]',
                  link: '/camera-packages'
                },
                {
                  icon: 'ri-key-2-line',
                  title: 'Access Control Systems',
                  description: 'Modern card access solutions for businesses, providing secure entry management and detailed access logs.',
                  bg: 'bg-[#0A1628]/5',
                  color: 'text-[#0A1628]',
                  link: '/access-control'
                },
                {
                  icon: 'ri-live-line',
                  title: 'Live Video Monitoring',
                  description: 'Real-time video surveillance with remote access, allowing you to watch your property from anywhere, anytime.',
                  bg: 'bg-[#D4A853]/10',
                  color: 'text-[#D4A853]',
                  link: '/live-monitoring'
                }
              ].map((service, index) => (
                <div 
                  key={index}
                  className={`${service.bg} rounded-2xl sm:rounded-3xl p-6 sm:p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer`}
                  style={servicesCards.getItemStyle(index)}
                >
                  <div className={`w-12 h-12 sm:w-16 sm:h-16 bg-[#D4A853] rounded-full flex items-center justify-center mb-4 sm:mb-6`}>
                    <i className={`${service.icon} text-2xl sm:text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-[#0A1628] mb-3 sm:mb-4">{service.title}</h3>
                  <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 leading-relaxed">{service.description}</p>
                  <Link to={service.link} className="text-[#D4A853] font-semibold flex items-center gap-2 hover:gap-3 transition-all cursor-pointer whitespace-nowrap text-sm sm:text-base">
                    Learn More <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Products Section */}
        <section id="products" className="py-12 sm:py-16 md:py-24 bg-[#F8F9FA]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div 
              ref={productsHeader.ref}
              className="text-center mb-10 sm:mb-16"
              style={{
                opacity: productsHeader.isVisible ? 1 : 0,
                transform: productsHeader.isVisible ? 'translateY(0)' : 'translateY(40px)',
                transition: 'opacity 0.7s ease, transform 0.7s ease',
              }}
            >
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-6 py-2 rounded-full text-sm font-semibold mb-4 whitespace-nowrap">
                PREMIUM EQUIPMENT
              </span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#0A1628] mb-4">
                Industry-Leading Security Products
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
                We partner with top manufacturers to deliver reliable, cutting-edge security technology
              </p>
            </div>

            <div ref={productsCards.ref} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8" data-product-shop>
              {[
                {
                  name: 'Pro Alarm System',
                  category: 'Alarm Systems',
                  image: 'https://readdy.ai/api/search-image?query=modern%20white%20wireless%20alarm%20control%20panel%20with%20touchscreen%20display%20mounted%20on%20clean%20white%20wall%20sleek%20minimalist%20design%20professional%20product%20photography%20with%20soft%20shadows%20simple%20background&width=600&height=600&seq=prod1&orientation=squarish',
                  features: ['Wireless Installation', 'Mobile App Control', 'Smart Home Integration', 'Battery Backup']
                },
                {
                  name: '4K Security Camera',
                  category: 'Security Cameras',
                  image: 'https://readdy.ai/api/search-image?query=professional%20white%204K%20security%20camera%20with%20sleek%20dome%20design%20high%20quality%20product%20shot%20on%20clean%20white%20background%20modern%20surveillance%20equipment%20with%20subtle%20reflections&width=600&height=600&seq=prod2&orientation=squarish',
                  features: ['4K Ultra HD', 'Night Vision', 'Motion Detection', 'Weather Resistant']
                },
                {
                  name: 'Smart Card Reader',
                  category: 'Access Control',
                  image: 'https://readdy.ai/api/search-image?query=modern%20black%20card%20access%20reader%20with%20LED%20indicator%20mounted%20on%20white%20wall%20professional%20commercial%20security%20device%20clean%20product%20photography%20simple%20background&width=600&height=600&seq=prod3&orientation=squarish',
                  features: ['Multi-Format Support', 'Encrypted Communication', 'Audit Trail Logging', 'Remote Management']
                }
              ].map((product, index) => (
                <div 
                  key={index} 
                  className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
                  style={productsCards.getItemStyle(index)}
                >
                  <div className="relative w-full h-52 sm:h-64 md:h-80 bg-gray-100">
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="w-full h-full object-cover object-center"
                    />
                    <span className="absolute top-4 left-4 bg-[#D4A853] text-white px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap">
                      {product.category}
                    </span>
                  </div>
                  <div className="p-5 sm:p-6">
                    <h3 className="text-xl sm:text-2xl font-bold text-[#0A1628] mb-3 sm:mb-4">{product.name}</h3>
                    <ul className="space-y-2 mb-4 sm:mb-6">
                      {product.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-gray-600 text-sm">
                          <i className="ri-check-line text-[#D4A853] text-lg"></i>
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <div className="flex items-center justify-end">
                      <a 
                        href="#contact"
                        onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}
                        className="bg-[#0A1628] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#D4A853] transition-all cursor-pointer whitespace-nowrap"
                      >
                        Get Quote
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Partners Section */}
            <div 
              ref={partnersSection.ref}
              className="mt-12 sm:mt-16 md:mt-24"
              style={{
                opacity: partnersSection.isVisible ? 1 : 0,
                transform: partnersSection.isVisible ? 'translateY(0)' : 'translateY(40px)',
                transition: 'opacity 0.7s ease, transform 0.7s ease',
              }}
            >
              <div className="text-center mb-8 sm:mb-12">
                <span className="inline-block bg-[#0A1628]/10 text-[#0A1628] px-6 py-2 rounded-full text-sm font-semibold mb-4 whitespace-nowrap">
                  TRUSTED PARTNERS
                </span>
                <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#0A1628] mb-4">
                  We Work With Industry Leaders
                </h3>
                <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
                  Partnering with the world's most trusted security brands to deliver exceptional protection
                </p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4 md:gap-6">
                {[
                  { name: 'Honeywell', image: 'https://readdy.ai/api/search-image?query=Honeywell%20corporate%20logo%20red%20text%20on%20clean%20white%20background%20professional%20brand%20identity%20simple%20minimalist%20design%20high%20resolution&width=300&height=200&seq=partner1&orientation=landscape', scale: 'scale-[1.32]' },
                  { name: 'DSC', image: 'https://readdy.ai/api/search-image?query=DSC%20security%20systems%20corporate%20logo%20blue%20text%20on%20clean%20white%20background%20professional%20brand%20identity%20simple%20minimalist%20design%20high%20resolution&width=300&height=200&seq=partner2&orientation=landscape', scale: 'scale-[1.32]' },
                  { name: 'Qolsys', image: 'https://readdy.ai/api/search-image?query=Qolsys%20smart%20security%20corporate%20logo%20modern%20text%20on%20clean%20white%20background%20professional%20brand%20identity%20simple%20minimalist%20design%20high%20resolution&width=300&height=200&seq=partner3&orientation=landscape', scale: 'scale-[1.32]' },
                  { name: 'Kantech', image: 'https://readdy.ai/api/search-image?query=Kantech%20access%20control%20corporate%20logo%20bold%20professional%20text%20on%20clean%20white%20background%20brand%20identity%20simple%20minimalist%20design%20high%20resolution&width=300&height=200&seq=partner4&orientation=landscape', scale: 'scale-[1.6]' },
                  { name: 'Hartmann', image: 'https://readdy.ai/api/search-image?query=Hartmann%20security%20corporate%20logo%20professional%20text%20on%20clean%20white%20background%20brand%20identity%20simple%20minimalist%20design%20high%20resolution&width=300&height=200&seq=partner5&orientation=landscape', scale: 'scale-[1.32]' },
                  { name: 'Hikvision', image: 'https://readdy.ai/api/search-image?query=Hikvision%20surveillance%20corporate%20logo%20red%20text%20on%20clean%20white%20background%20professional%20brand%20identity%20simple%20minimalist%20design%20high%20resolution&width=300&height=200&seq=partner6&orientation=landscape', scale: 'scale-[1.32]' },
                  { name: 'Hanwha', image: 'https://readdy.ai/api/search-image?query=Hanwha%20Vision%20corporate%20logo%20orange%20text%20on%20clean%20white%20background%20professional%20brand%20identity%20simple%20minimalist%20design%20high%20resolution&width=300&height=200&seq=partner7&orientation=landscape', scale: 'scale-[1.32]' },
                  { name: 'Aibase', image: 'https://readdy.ai/api/search-image?query=Aibase%20smart%20home%20corporate%20logo%20modern%20text%20on%20clean%20white%20background%20professional%20brand%20identity%20simple%20minimalist%20design%20high%20resolution&width=300&height=200&seq=partner8&orientation=landscape', scale: 'scale-[1.32]' },
                  { name: 'Dahua', image: 'https://readdy.ai/api/search-image?query=Dahua%20Technology%20corporate%20logo%20blue%20text%20on%20clean%20white%20background%20professional%20brand%20identity%20simple%20minimalist%20design%20high%20resolution&width=300&height=200&seq=partner9&orientation=landscape', scale: 'scale-[1.32]' },
                  { name: 'Axis', image: 'https://readdy.ai/api/search-image?query=Axis%20Communications%20corporate%20logo%20bold%20black%20text%20on%20clean%20white%20background%20professional%20brand%20identity%20simple%20minimalist%20design%20high%20resolution&width=300&height=200&seq=partner10&orientation=landscape', scale: 'scale-[1.6]' },
                  { name: 'Exacqvision', image: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/3d0f4ceb902de47fdc96bd10d5f1e962.png', scale: 'scale-[1.32]' },
                  { name: 'Alarm.com', image: 'https://readdy.ai/api/search-image?query=Alarm.com%20smart%20security%20corporate%20logo%20blue%20text%20on%20clean%20white%20background%20professional%20brand%20identity%20simple%20minimalist%20design%20high%20resolution&width=300&height=200&seq=partner12&orientation=landscape', scale: 'scale-[1.32]' }
                ].map((partner, index) => (
                  <div 
                    key={index}
                    className="bg-white rounded-xl p-4 sm:p-6 md:p-8 shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center h-20 sm:h-24 md:h-28 border border-gray-100 hover:border-[#D4A853]/30"
                  >
                    <img 
                      src={partner.image}
                      alt={partner.name}
                      className={`max-w-full max-h-full object-contain grayscale hover:grayscale-0 transition-all duration-300 ${partner.scale}`}
                    />
                  </div>
                ))}
              </div>

              <div className="mt-8 sm:mt-12 text-center">
                <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base">Certified installers and authorized dealers for all major security brands</p>
                <div className="flex items-center justify-center gap-4 sm:gap-8 flex-wrap">
                  <div className="flex items-center gap-2">
                    <i className="ri-shield-check-line text-xl sm:text-2xl text-[#D4A853]"></i>
                    <span className="text-[80px] sm:text-[100px] md:text-[140px] lg:text-[180px] font-extrabold text-[#D4A853]/20 leading-none">25</span>
                    <div className="lg:absolute lg:top-1/2 lg:left-full lg:ml-6 lg:-translate-y-1/2">
                      <p className="text-lg sm:text-xl md:text-2xl font-bold text-[#0A1628] whitespace-nowrap">YEARS OF</p>
                      <p className="text-lg sm:text-xl md:text-2xl font-bold text-[#0A1628] whitespace-nowrap">EXCELLENCE</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* About/Credibility Section */}
        <section id="about" className="py-12 sm:py-16 md:py-24 bg-[#F8F9FA]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 md:gap-12 items-center">
              <div 
                ref={aboutLeft.ref}
                className="lg:col-span-2 flex flex-col items-center lg:items-start"
                style={{
                  opacity: aboutLeft.isVisible ? 1 : 0,
                  transform: aboutLeft.isVisible ? 'translateX(0)' : 'translateX(-50px)',
                  transition: 'opacity 0.8s ease, transform 0.8s ease',
                }}
              >
                <div className="flex flex-col items-center lg:items-start mb-6 sm:mb-8">
                  <div className="flex items-center gap-4 lg:relative">
                    <span className="text-[80px] sm:text-[100px] md:text-[140px] lg:text-[180px] font-extrabold text-[#D4A853]/20 leading-none">25</span>
                    <div className="lg:absolute lg:top-1/2 lg:left-full lg:ml-6 lg:-translate-y-1/2">
                      <p className="text-lg sm:text-xl md:text-2xl font-bold text-[#0A1628] whitespace-nowrap">YEARS OF</p>
                      <p className="text-lg sm:text-xl md:text-2xl font-bold text-[#0A1628] whitespace-nowrap">EXCELLENCE</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-2xl px-6 sm:px-8 py-3 sm:py-4 shadow-md mb-6">
                  <span className="text-xl sm:text-2xl md:text-3xl font-serif text-[#0A1628]">EST. 1999</span>
                </div>
                <div className="bg-white rounded-2xl px-6 sm:px-8 py-4 sm:py-5 shadow-md border-2 border-[#0A1628]/10">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#003DA5] rounded-lg flex items-center justify-center flex-shrink-0">
                      <div className="text-center">
                        <div className="text-2xl sm:text-3xl font-extrabold text-white leading-none">A+</div>
                        <div className="text-[8px] sm:text-[9px] text-white/90 font-semibold tracking-tight">RATING</div>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm font-bold text-[#0A1628] mb-0.5 leading-tight">Better Business Bureau</p>
                    </div>
                  </div>
                </div>
              </div>

              <div 
                ref={aboutRight.ref}
                className="lg:col-span-3"
                style={{
                  opacity: aboutRight.isVisible ? 1 : 0,
                  transform: aboutRight.isVisible ? 'translateX(0)' : 'translateX(50px)',
                  transition: 'opacity 0.8s ease, transform 0.8s ease',
                }}
              >
                <span className="inline-block bg-[#D4A853] text-white text-sm font-semibold mb-4 whitespace-nowrap">WHY CHOOSE US</span>
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#0A1628] mb-4 sm:mb-6">
                  Industry-Leading Security Expertise
                </h2>
                <p className="text-gray-600 text-base sm:text-lg mb-4 leading-relaxed">
                  For over 25 years, we've been at the forefront of security technology, protecting homes and businesses with state-of-the-art alarm systems, surveillance cameras, and access control solutions. Our commitment to excellence has earned us the trust of over 30,000 satisfied clients.
                </p>
                <p className="text-gray-600 text-base sm:text-lg mb-6 sm:mb-8 leading-relaxed">
                  Every member of our team is factory-certified and continuously trained on the latest security technologies. We don't just install systems—we create comprehensive security solutions tailored to your unique needs.
                </p>

                <div ref={aboutStats.ref} className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
                  {[
                    { number: '30,000+', label: 'Installations' },
                    { number: '98%', label: 'Customer Satisfaction' },
                    { number: '100%', label: 'Licensed & Insured' },
                    { number: '24/7', label: 'Expert Support' }
                  ].map((stat, index) => (
                    <div 
                      key={index} 
                      className="text-center"
                      style={aboutStats.getItemStyle(index)}
                    >
                      <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#D4A853] mb-1 sm:mb-2">{stat.number}</p>
                      <p className="text-xs sm:text-sm text-gray-600">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-12 sm:py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-16">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-6 py-2 rounded-full text-sm font-semibold mb-4 whitespace-nowrap">
                OUR ADVANTAGES
              </span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#0A1628] mb-4">
                Why Choose Us for Your Security Needs
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
                Experience the difference that 25 years of expertise makes
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {[
                {
                  icon: 'ri-shield-star-line',
                  title: 'Factory-Certified Experts',
                  description: 'Our technicians are certified by all major security brands including Honeywell, DSC, Hikvision, and more. We stay current with the latest technology through continuous training.'
                },
                {
                  icon: 'ri-time-line',
                  title: 'Fast Response Time',
                  description: 'Same-day service available across the Greater Toronto Area. Emergency support available 24/7 because your security can\'t wait.'
                },
                {
                  icon: 'ri-award-line',
                  title: '25 Years of Excellence',
                  description: 'Since 1999, we\'ve completed over 30,000 installations. Our longevity speaks to our reliability and commitment to customer satisfaction.'
                },
                {
                  icon: 'ri-customer-service-2-line',
                  title: 'Lifetime Support',
                  description: 'We don\'t disappear after installation. Enjoy ongoing technical support, system maintenance, and upgrades for the life of your system.'
                },
                {
                  icon: 'ri-price-tag-3-line',
                  title: 'Transparent Pricing',
                  description: 'No hidden fees or surprise charges. We provide detailed quotes upfront and honor our commitments. Quality security at fair prices.'
                },
                {
                  icon: 'ri-smartphone-line',
                  title: 'Smart Technology',
                  description: 'Control your entire security system from your smartphone. Receive instant alerts, view live camera feeds, manage access from anywhere in the world.'
                },
                {
                  icon: 'ri-shield-check-line',
                  title: 'Licensed & Insured',
                  description: '100% licensed and fully insured for your protection. We meet all industry standards and regulations for professional security installation.'
                },
                {
                  icon: 'ri-team-line',
                  title: 'Local Family Business',
                  description: 'We\'re not a faceless corporation. As a local family-owned business, we treat every customer like family and take pride in protecting our community.'
                },
                {
                  icon: 'ri-tools-line',
                  title: 'Custom Solutions',
                  description: 'Every property is unique. We design tailored security solutions based on your specific needs, budget, and property layout—not one-size-fits-all packages.'
                }
              ].map((feature, index) => (
                <div 
                  key={index}
                  className="bg-[#F8F9FA] rounded-2xl p-6 sm:p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100"
                >
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#D4A853] rounded-full flex items-center justify-center mb-4 sm:mb-6">
                    <i className={`${feature.icon} text-2xl sm:text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-[#0A1628] mb-3 sm:mb-4">{feature.title}</h3>
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>

            <div className="mt-10 sm:mt-16 text-center">
              <button 
                onClick={scrollToContact}
                className="bg-[#D4A853] text-white px-8 sm:px-10 py-3.5 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap shadow-lg"
              >
                Experience the Difference
              </button>
            </div>
          </div>
        </section>

        {/* Security Statistics & Industry Insights - NEW SHAREABLE CONTENT */}
        <section className="py-12 sm:py-16 md:py-24 bg-gradient-to-br from-[#0A1628] via-[#0d1f3a] to-[#0A1628] text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }}></div>
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
            <div className="text-center mb-10 sm:mb-16">
              <span className="inline-block bg-[#D4A853] text-white px-6 py-2 rounded-full text-sm font-semibold mb-4 whitespace-nowrap">
                INDUSTRY INSIGHTS
              </span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
                Home Security Statistics You Need to Know
              </h2>
              <p className="text-base sm:text-lg text-white/80 max-w-3xl mx-auto">
                Understanding the facts about home security helps you make informed decisions to protect what matters most
              </p>
            </div>

            {/* Key Statistics Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12 sm:mb-16">
              {[
                { 
                  stat: '300%', 
                  label: 'Higher Break-in Risk', 
                  description: 'Homes without security systems are 300% more likely to be targeted by burglars',
                  icon: 'ri-alarm-warning-line',
                  source: 'University of North Carolina Study'
                },
                { 
                  stat: '60%', 
                  label: 'Burglars Deterred', 
                  description: 'Over 60% of convicted burglars say they would avoid homes with visible security systems',
                  icon: 'ri-shield-check-line',
                  source: 'FBI Uniform Crime Report'
                },
                { 
                  stat: '34 Sec', 
                  label: 'Average Break-in Time', 
                  description: 'The average burglar takes just 34 seconds to break into a home without security',
                  icon: 'ri-time-line',
                  source: 'Security Industry Association'
                },
                { 
                  stat: '$2,800', 
                  label: 'Average Loss Per Break-in', 
                  description: 'The average financial loss from a home burglary in Canada is $2,800',
                  icon: 'ri-money-dollar-circle-line',
                  source: 'Statistics Canada'
                }
              ].map((item, index) => (
                <div key={index} className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:bg-white/10 transition-all">
                  <div className="w-14 h-14 bg-[#D4A853] rounded-xl flex items-center justify-center mb-4">
                    <i className={`${item.icon} text-2xl text-white`}></i>
                  </div>
                  <p className="text-4xl sm:text-5xl font-bold text-[#D4A853] mb-2">{item.stat}</p>
                  <p className="text-white/70 text-sm">{item.label}</p>
                  <p className="text-xs text-white/50 italic">Source: {item.source}</p>
                </div>
              ))}
            </div>

            {/* Crime Statistics by Time */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 sm:p-10 border border-white/10 mb-12">
              <h3 className="text-2xl sm:text-3xl font-bold mb-6 text-center">When Do Break-ins Happen?</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-20 h-20 bg-[#D4A853]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-sun-line text-3xl text-[#D4A853]"></i>
                  </div>
                  <p className="text-3xl font-bold text-[#D4A853] mb-2">65%</p>
                  <h4 className="font-bold mb-2">Daytime (6am-6pm)</h4>
                  <p className="text-sm text-white/70">Most burglaries occur during the day when homes are empty</p>
                </div>
                <div className="text-center">
                  <div className="w-20 h-20 bg-[#D4A853]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-calendar-line text-3xl text-[#D4A853]"></i>
                  </div>
                  <p className="text-3xl font-bold text-[#D4A853] mb-2">Summer</p>
                  <h4 className="font-bold mb-2">Peak Season</h4>
                  <p className="text-sm text-white/70">July and August see the highest rates of home burglaries</p>
                </div>
                <div className="text-center">
                  <div className="w-20 h-20 bg-[#D4A853]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-door-line text-3xl text-[#D4A853]"></i>
                  </div>
                  <p className="text-3xl font-bold text-[#D4A853] mb-2">34%</p>
                  <h4 className="font-bold mb-2">Front Door Entry</h4>
                  <p className="text-sm text-white/70">Over one-third of burglars enter through the front door</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Complete Security Buying Guide - NEW SHAREABLE CONTENT */}
        <section className="py-12 sm:py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-16">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-6 py-2 rounded-full text-sm font-semibold mb-4 whitespace-nowrap">
                EXPERT GUIDE
              </span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#0A1628] mb-4">
                Complete Home Security Buying Guide 2024
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
                Everything you need to know before choosing a security system for your home or business
              </p>
            </div>

            {/* Guide Content */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Step-by-Step Guide */}
              <div className="lg:col-span-2 space-y-6">
                {[
                  {
                    step: 1,
                    title: 'Assess Your Security Needs',
                    icon: 'ri-search-eye-line',
                    content: [
                      { subtitle: 'Property Type', text: 'Single-family homes need different coverage than apartments or commercial buildings. Consider entry points, square footage, and property layout.' },
                      { subtitle: 'Risk Factors', text: 'Evaluate your neighborhood crime rate, property value, and whether you have high-value items. Check local police statistics for your area.' },
                      { subtitle: 'Lifestyle Considerations', text: 'Do you travel frequently? Work from home? Have children or pets? These factors determine which features you need most.' }
                    ]
                  },
                  {
                    step: 2,
                    title: 'Choose Your System Type',
                    icon: 'ri-settings-3-line',
                    content: [
                      { subtitle: 'Wired vs. Wireless', text: 'Wireless systems are easier to install and relocate. Wired systems are more reliable but require professional installation and can\'t be moved.' },
                      { subtitle: 'Smart Home Integration', text: 'Modern systems integrate with Alexa, Google Home, and smart locks. Consider compatibility with devices you already own.' }
                    ]
                  },
                  {
                    step: 3,
                    title: 'Essential Components to Include',
                    icon: 'ri-list-check',
                    content: [
                      { subtitle: 'Door & Window Sensors', text: 'The foundation of any system. Install on all ground-floor entry points and accessible windows. Average home needs 10-15 sensors.' },
                      { subtitle: 'Motion Detectors', text: 'Place in hallways and main rooms. Use pet-immune sensors if you have animals under 80 lbs. Avoid pointing at windows or heat sources.' },
                      { subtitle: 'Security Cameras', text: 'Minimum: Front door, back door, and driveway. Upgrade: Side entrances, garage, and interior common areas. Choose 4MP+ resolution for clear footage.' }
                    ]
                  },
                  {
                    step: 4,
                    title: 'Verify Provider Credentials',
                    icon: 'ri-shield-star-line',
                    content: [
                      { subtitle: 'Licensing & Insurance', text: 'Verify the company holds proper security dealer licenses and carries liability insurance. Check with Better Business Bureau for ratings.' },
                      { subtitle: 'Certifications', text: 'Look for factory certifications from manufacturers like Honeywell, DSC, or Qolsys. Technicians should be trained and certified.' },
                      { subtitle: 'Local Presence', text: 'Choose companies with local offices for faster service. National chains may have slower response times for repairs and support.' }
                    ]
                  }
                ].map((item, index) => (
                  <div key={index} className="bg-[#F8F9FA] rounded-2xl p-6 sm:p-8 border border-gray-100 hover:border-[#D4A853]/30 transition-all">
                    <div className="flex items-start gap-4 mb-6">
                      <div className="w-14 h-14 bg-[#D4A853] rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-2xl text-white`}></i>
                      </div>
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <span className="bg-[#0A1628] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold">
                            {item.step}
                          </span>
                          <h3 className="text-xl sm:text-2xl font-bold text-[#0A1628]">{item.title}</h3>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-4 pl-0 sm:pl-[4.5rem]">
                      {item.content.map((section, idx) => (
                        <div key={idx}>
                          <h4 className="font-bold text-[#0A1628] mb-2 flex items-center gap-2">
                            <i className="ri-arrow-right-s-line text-[#D4A853]"></i>
                            {section.subtitle}
                          </h4>
                          <p className="text-gray-600 text-sm leading-relaxed">{section.text}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Quick Reference Sidebar */}
              <div className="space-y-6">
                {/* Average Costs Reference */}
                <div className="bg-[#D4A853] text-white rounded-2xl p-6">
                  <h3 className="text-xl font-bold mb-4">2024 Average Costs</h3>
                  <div className="space-y-4 text-sm">
                    <div>
                      <p className="text-white/60 mb-1">Basic System</p>
                      <p className="text-2xl font-bold text-[#0A1628]">$29-39/mo</p>
                    </div>
                    <div>
                      <p className="text-white/60 mb-1">Mid-Range System</p>
                      <p className="text-2xl font-bold text-[#0A1628]">$40-59/mo</p>
                    </div>
                    <div>
                      <p className="text-white/60 mb-1">Premium System</p>
                      <p className="text-2xl font-bold text-[#0A1628]">$60-80/mo</p>
                    </div>
                    <div className="pt-4 border-t border-white/20">
                      <p className="text-white/80 text-xs">Prices include professional monitoring, equipment, and installation</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-12 text-center bg-gradient-to-r from-[#0A1628] to-[#1a2d4a] rounded-2xl p-8 sm:p-12">
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">Ready to Get Started?</h3>
              <p className="text-white/80 mb-6 max-w-2xl mx-auto">
                Get a free, no-obligation security assessment from our certified experts. We'll help you choose the perfect system for your needs and budget.
              </p>
              <button 
                onClick={scrollToContact}
                className="bg-[#D4A853] text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap shadow-xl"
              >
                Schedule Free Consultation
              </button>
            </div>
          </div>
        </section>

        {/* Blog CTA Section */}
        <section className="py-12 sm:py-16 md:py-20 bg-[#F8F9FA]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              {/* Left - Text */}
              <div>
                <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-6 py-2 rounded-full text-sm font-semibold mb-4 whitespace-nowrap">
                  SECURITY INSIGHTS
                </span>
                <h2 className="text-3xl sm:text-4xl font-bold text-[#0A1628] mb-4">
                  Expert Security Tips & Industry News
                </h2>
                <p className="text-base text-gray-600 mb-6 leading-relaxed">
                  Stay ahead of threats with our expert guides, product comparisons, real-world case studies, and the latest security industry news — all written by our certified professionals.
                </p>
                <div className="flex flex-wrap gap-3 mb-8">
                  {['Security Tips', 'Product Reviews', 'How-To Guides', 'Case Studies', 'Industry News'].map((tag) => (
                    <span key={tag} className="bg-white border border-gray-200 text-gray-600 px-4 py-1.5 rounded-full text-xs font-semibold">
                      {tag}
                    </span>
                  ))}
                </div>
                <Link
                  to="/blog"
                  className="inline-flex items-center gap-3 bg-[#D4A853] text-white px-8 py-3.5 rounded-full font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap text-sm sm:text-base"
                >
                  Visit Our Blog
                  <i className="ri-arrow-right-line text-lg"></i>
                </Link>
              </div>

              {/* Right - Recent Post Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  {
                    category: 'Security Tips',
                    categoryColor: 'bg-emerald-100 text-emerald-700',
                    title: '10 Home Security Tips Every GTA Homeowner Should Know',
                    readTime: 8,
                    slug: '10-home-security-tips-gta',
                    image: 'https://readdy.ai/api/search-image?query=modern%20suburban%20home%20exterior%20at%20dusk%20with%20warm%20porch%20lights%20glowing%20security%20camera%20visible%20on%20wall%20professional%20residential%20photography%20clean%20architecture%20golden%20hour%20lighting&width=600&height=400&seq=blogcta1&orientation=landscape',
                  },
                  {
                    category: 'Product Reviews',
                    categoryColor: 'bg-amber-100 text-amber-700',
                    title: 'Hikvision vs Dahua: Which Security Camera Brand Is Better?',
                    readTime: 10,
                    slug: 'hikvision-vs-dahua-comparison',
                    image: 'https://readdy.ai/api/search-image?query=two%20professional%20security%20cameras%20side%20by%20side%20on%20white%20background%20product%20comparison%20photography%20clean%20studio%20lighting%20high%20resolution%20detail%20shot&width=600&height=400&seq=blogcta2&orientation=landscape',
                  },
                  {
                    category: 'How-To Guides',
                    categoryColor: 'bg-violet-100 text-violet-700',
                    title: 'How to Choose the Right Security Camera Placement',
                    readTime: 7,
                    slug: 'security-camera-installation-guide',
                    image: 'https://readdy.ai/api/search-image?query=professional%20technician%20installing%20security%20camera%20on%20exterior%20wall%20of%20modern%20building%20daylight%20clear%20sky%20residential%20installation%20process&width=600&height=400&seq=blogcta3&orientation=landscape',
                  },
                  {
                    category: 'Industry News',
                    categoryColor: 'bg-sky-100 text-sky-700',
                    title: 'AI-Powered Security Cameras: The Future of Surveillance',
                    readTime: 6,
                    slug: 'ai-security-cameras-2024-trends',
                    image: 'https://readdy.ai/api/search-image?query=futuristic%20AI%20security%20camera%20with%20digital%20overlay%20interface%20smart%20detection%20technology%20modern%20surveillance%20system%20glowing%20blue%20light%20tech%20aesthetic&width=600&height=400&seq=blogcta4&orientation=landscape',
                  },
                ].map((post, index) => (
                  <Link
                    key={index}
                    to={`/blog/${post.slug}`}
                    className="bg-white rounded-xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer group"
                  >
                    <div className="w-full h-32 overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${post.categoryColor}`}>
                        {post.category}
                      </span>
                      <h3 className="text-sm font-bold text-[#0A1628] mt-2 mb-1 leading-snug line-clamp-2 group-hover:text-[#D4A853] transition-colors">
                        {post.title}
                      </h3>
                      <p className="text-xs text-gray-400 flex items-center gap-1">
                        <i className="ri-time-line"></i> {post.readTime} min read
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* App Integration Section */}
        <section id="app-integration" className="py-12 sm:py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 md:gap-16 items-center">
              <div
                ref={appText.ref}
                style={{
                  opacity: appText.isVisible ? 1 : 0,
                  transform: appText.isVisible ? 'translateX(0)' : 'translateX(-50px)',
                  transition: 'opacity 0.8s ease, transform 0.8s ease',
                }}
              >
                <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-6 py-2 rounded-full text-sm font-semibold mb-4 whitespace-nowrap">
                  SMART TECHNOLOGY
                </span>
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#0A1628] mb-4 sm:mb-6">
                  Control Your Security from Anywhere
                </h2>
                <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8 leading-relaxed">
                  Our advanced mobile app puts complete control of your security system in the palm of your hand. Arm or disarm your alarm, view live camera feeds, manage access permissions, and receive instant alerts—all from your smartphone or tablet.
                </p>
                
                <div className="space-y-4 sm:space-y-6 mb-6 sm:mb-8">
                  {[
                    { icon: 'ri-smartphone-line', title: 'Mobile App Control', description: 'Manage your entire security system from iOS or Android devices' },
                    { icon: 'ri-notification-3-line', title: 'Instant Alerts', description: 'Receive real-time notifications for any security events' },
                    { icon: 'ri-video-line', title: 'Live Video Streaming', description: 'Watch live feeds from all your security cameras remotely' },
                    { icon: 'ri-history-line', title: 'Activity History', description: 'Review detailed logs of all system activities and events' }
                  ].map((feature, index) => (
                    <div key={index} className="flex items-start gap-3 sm:gap-4">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#D4A853]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className={`${feature.icon} text-xl sm:text-2xl text-[#D4A853]`}></i>
                      </div>
                      <div>
                        <h3 className="text-lg sm:text-xl font-bold text-[#0A1628] mb-1 sm:mb-2">{feature.title}</h3>
                        <p className="text-sm sm:text-base text-gray-600">{feature.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div 
                ref={appImage.ref}
                className="relative order-first lg:order-last"
                style={{
                  opacity: appImage.isVisible ? 1 : 0,
                  transform: appImage.isVisible ? 'translateX(0) scale(1)' : 'translateX(50px) scale(0.95)',
                  transition: 'opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s',
                }}
              >
                <div className="relative w-full h-[350px] sm:h-[450px] md:h-[600px]">
                  <img 
                    src="https://readdy.ai/api/search-image?query=modern%20smartphone%20displaying%20security%20app%20interface%20with%20camera%20feeds%20and%20alarm%20controls%20on%20screen%20professional%20product%20mockup%20on%20clean%20gradient%20background%20sleek%20mobile%20application%20design&width=800&height=1000&seq=app1&orientation=portrait" 
                    alt="Security App Interface"
                    className="w-full h-full object-contain object-center"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Live Video Monitoring Section */}
        <section id="live-monitoring" className="py-12 sm:py-16 md:py-24 bg-[#0A1628] text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img 
              src="https://readdy.ai/api/search-image?query=blurred%20modern%20security%20operations%20center%20with%20monitors%20and%20equipment%20dark%20blue%20ambient%20lighting%20professional%20technology%20environment%20out%20of%20focus%20background&width=1920&height=1080&seq=contact1&orientation=landscape" 
              alt="Contact Background"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b sm:bg-gradient-to-r from-[#0A1628]/95 via-[#0A1628]/90 to-[#0A1628]/80 sm:to-transparent"></div>
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
            <div className="max-w-5xl mx-auto">
              <div 
                ref={monitoringHeader.ref}
                className="text-center mb-10 sm:mb-16"
                style={{
                  opacity: monitoringHeader.isVisible ? 1 : 0,
                  transform: monitoringHeader.isVisible ? 'translateY(0)' : 'translateY(40px)',
                  transition: 'opacity 0.7s ease, transform 0.7s ease',
                }}
              >
                <span className="inline-block bg-[#D4A853] text-white px-6 py-2 rounded-full text-sm font-semibold mb-4 whitespace-nowrap">
                  REAL-TIME PROTECTION
                </span>
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6">
                  Professional Live Video Monitoring
                </h2>
                <p className="text-base sm:text-lg md:text-xl text-white/80 max-w-3xl mx-auto">
                  Watch your property in real-time from anywhere with crystal-clear HD video streaming and intelligent motion detection
                </p>
              </div>

              <div ref={monitoringCards.ref} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
                {[
                  { icon: 'ri-live-line', title: 'Live HD Streaming', description: 'Access crystal-clear live video feeds from all your cameras instantly through our mobile app or web portal.', stat: '4K', statLabel: 'Ultra HD Quality' },
                  { icon: 'ri-notification-3-line', title: 'Smart Alerts', description: 'Receive instant notifications when motion is detected, with video clips automatically saved to the cloud.', stat: 'Instant', statLabel: 'Notifications' },
                  { icon: 'ri-cloud-line', title: 'Cloud Recording', description: 'All footage is securely stored in the cloud with easy access to historical recordings anytime, anywhere.', stat: '30 Days', statLabel: 'Storage' }
                ].map((item, index) => (
                  <div 
                    key={index} 
                    className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-white/10 hover:bg-white/10 transition-all"
                    style={monitoringCards.getItemStyle(index)}
                  >
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#D4A853] rounded-full flex items-center justify-center mb-4 sm:mb-6">
                      <i className={`${item.icon} text-2xl sm:text-3xl text-white`}></i>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">{item.title}</h3>
                    <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 leading-relaxed">{item.description}</p>
                    <div className="pt-4 sm:pt-6 border-t border-white/10">
                      <p className="text-3xl sm:text-4xl font-bold text-[#D4A853] mb-1">{item.stat}</p>
                      <p className="text-xs text-white/60">{item.statLabel}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div 
                ref={monitoringCta.ref}
                className="bg-gradient-to-r from-[#0A1628] to-[#1a2d4a] rounded-2xl p-6 sm:p-8 md:p-12"
                style={{
                  opacity: monitoringCta.isVisible ? 1 : 0,
                  transform: monitoringCta.isVisible ? 'scale(1)' : 'scale(0.95)',
                  transition: 'opacity 0.7s ease 0.2s, transform 0.7s ease 0.2s',
                }}
              >
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-6">
                  <div className="text-center sm:text-left">
                    <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2">Don't See Your Area?</h3>
                    <p className="text-sm sm:text-base text-white/70">We may still be able to help! Contact us to discuss your location.</p>
                  </div>
                  <button 
                    onClick={() => scrollToSection('contact')}
                    className="bg-[#D4A853] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap flex items-center gap-2"
                  >
                    <i className="ri-phone-line"></i>
                    Contact Us Today
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonial Section */}
        <section className="py-12 sm:py-16 md:py-24 bg-[#0A1628]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div 
              ref={testimonialSection.ref}
              className="max-w-5xl mx-auto"
              style={{
                opacity: testimonialSection.isVisible ? 1 : 0,
                transform: testimonialSection.isVisible ? 'translateY(0)' : 'translateY(40px)',
                transition: 'opacity 0.8s ease, transform 0.8s ease',
              }}
            >
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl sm:rounded-3xl overflow-hidden border border-white/10">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-0">
                  <div className="md:col-span-2 relative h-48 sm:h-64 md:h-auto">
                    <div className="absolute inset-0 w-full h-full">
                      <img 
                        src="https://readdy.ai/api/search-image?query=professional%20business%20owner%20standing%20confidently%20in%20modern%20restaurant%20interior%20warm%20lighting%20satisfied%20customer%20portrait%20residential%20setting&width=600&height=800&seq=testimonial1&orientation=portrait" 
                        alt="Michael Rodriguez"
                        className="w-full h-full object-cover object-center"
                      />
                    </div>
                  </div>
                  <div className="md:col-span-3 p-6 sm:p-8 md:p-12 flex flex-col justify-center">
                    <i className="ri-double-quotes-l text-4xl sm:text-5xl md:text-7xl text-[#D4A853] mb-4 sm:mb-6"></i>
                    <p className="text-base sm:text-lg md:text-2xl text-white leading-relaxed mb-6 sm:mb-8">
                      After comparing five security companies, we chose them for their expertise and technology. The app-controlled alarm system and live camera feeds give us complete peace of mind.
                    </p>
                    <div>
                      <p className="text-lg sm:text-xl font-bold text-white mb-1">Michael Rodriguez</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Customer Reviews Section */}
        <section className="py-12 sm:py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div 
              ref={reviewsHeader.ref}
              className="text-center mb-10 sm:mb-16"
              style={{
                opacity: reviewsHeader.isVisible ? 1 : 0,
                transform: reviewsHeader.isVisible ? 'translateY(0)' : 'translateY(40px)',
                transition: 'opacity 0.7s ease, transform 0.7s ease',
              }}
            >
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-6 py-2 rounded-full text-sm font-semibold mb-4 whitespace-nowrap">
                CLIENT TESTIMONIALS
              </span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#0A1628] mb-4">
                What Our Customers Say
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
                Real feedback from satisfied clients across the Greater Toronto Area
              </p>
            </div>

            <div ref={reviewsCards.ref} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
              {[
                { name: 'Sarah Chen', location: 'Mississauga, ON', rating: 5, image: 'https://readdy.ai/api/search-image?query=professional%20asian%20woman%20homeowner%20smiling%20in%20modern%20living%20room%20natural%20lighting%20happy%20customer%20portrait%20residential%20setting&width=400&height=400&seq=review1&orientation=squarish', review: 'We had our home security system installed three years ago and couldn\'t be happier. The 24/7 monitoring gives us peace of mind when we travel. The technicians were professional and explained everything clearly.', service: 'Alarm System & Monitoring' },
                { name: 'David Thompson', location: 'Toronto, ON', rating: 5, image: 'https://readdy.ai/api/search-image?query=professional%20caucasian%20businessman%20in%20office%20setting%20confident%20smile%20satisfied%20customer%20portrait%20corporate%20environment&width=400&height=400&seq=review2&orientation=squarish', review: 'Outstanding service from start to finish. They installed security cameras throughout our office building and the video quality is exceptional. The mobile app makes it easy to check in from anywhere.', service: 'Security Cameras' },
                { name: 'Priya Patel', location: 'Mississauga, ON', rating: 5, image: 'https://readdy.ai/api/search-image?query=professional%20south%20asian%20woman%20business%20owner%20in%20modern%20home%20office%20natural%20lighting%20satisfied%20customer%20portrait%20residential%20setting&width=400&height=400&seq=review3&orientation=squarish', review: 'The card access system they installed for our retail store has been flawless. We can easily manage employee access and track entry logs. Their support team is always responsive when we have questions.', service: 'Access Control' },
                { name: 'James Wilson', location: 'Toronto, ON', rating: 5, image: 'https://readdy.ai/api/search-image?query=professional%20middle%20aged%20man%20homeowner%20in%20modern%20kitchen%20smiling%20natural%20lighting%20happy%20customer%20portrait%20residential%20interior&width=400&height=400&seq=review4&orientation=squarish', review: 'After a break-in attempt in our neighborhood, we decided to upgrade our security. The team recommended the perfect system for our needs and budget. Installation was quick and the app integration is fantastic.', service: 'Complete Security Package' },
                { name: 'Maria Santos', location: 'Mississauga, ON', rating: 5, image: 'https://readdy.ai/api/search-image?query=professional%20hispanic%20woman%20business%20owner%20in%20modern%20home%20office%20natural%20lighting%20satisfied%20customer%20portrait%20residential%20setting&width=400&height=400&seq=review5&orientation=squarish', review: 'We had our home security system installed three years ago and couldn\'t be happier. The 24/7 monitoring gives us peace of mind when we travel. The technicians were professional and explained everything clearly.', service: 'Alarm System & Monitoring' },
                { name: 'Robert Kim', location: 'Toronto, ON', rating: 5, image: 'https://readdy.ai/api/search-image?query=professional%20asian%20man%20business%20owner%20in%20modern%20warehouse%20facility%20confident%20smile%20satisfied%20customer%20portrait%20industrial%20setting&width=400&height=400&seq=review6&orientation=squarish', review: 'We needed a comprehensive security solution for our warehouse and they delivered beyond expectations. The combination of cameras, alarms, and access control has significantly improved our security posture.', service: 'Commercial Security' }
              ].map((testimonial, index) => (
                <div 
                  key={index}
                  className="bg-[#F8F9FA] rounded-2xl p-5 sm:p-6 md:p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100"
                  style={reviewsCards.getItemStyle(index)}
                >
                  <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                    <div className="relative w-12 h-12 sm:w-16 sm:h-16 flex-shrink-0">
                      <img src={testimonial.image} alt={testimonial.name} className="w-full h-full object-cover object-center rounded-full" />
                    </div>
                    <div>
                      <h3 className="text-base sm:text-lg font-bold text-[#0A1628]">{testimonial.name}</h3>
                      <p className="text-xs sm:text-sm text-gray-500 flex items-center gap-1">
                        <i className="ri-map-pin-line text-[#D4A853]"></i>
                        {testimonial.location}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 mb-3 sm:mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-[#D4A853]"></i>
                    ))}
                  </div>
                  <p className="text-sm sm:text-base text-gray-600 mb-4 leading-relaxed">"{testimonial.review}"</p>
                  <div className="pt-3 sm:pt-4 border-t border-gray-200">
                    <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs font-semibold whitespace-nowrap">
                      {testimonial.service}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div 
              ref={reviewsStats.ref}
              className="mt-10 sm:mt-16 text-center"
              style={{
                opacity: reviewsStats.isVisible ? 1 : 0,
                transform: reviewsStats.isVisible ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.97)',
                transition: 'opacity 0.7s ease, transform 0.7s ease',
              }}
            >
              <div className="inline-flex items-center gap-8 bg-[#F8F9FA] rounded-2xl px-6 sm:px-12 py-6 sm:py-8">
                <div className="text-center">
                  <p className="text-sm sm:text-base text-gray-600 mb-2">Trusted by thousands of satisfied customers across the Greater Toronto Area</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Client Showcase Section */}
        <section className="py-12 sm:py-16 md:py-24 bg-[#F8F9FA]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-16">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-6 py-2 rounded-full text-sm font-semibold mb-4 whitespace-nowrap">
                TRUSTED BY INDUSTRY LEADERS
              </span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#0A1628] mb-4">
                Our Valued Clients
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
                Protecting businesses across diverse industries with reliable security solutions
              </p>
            </div>

            <div className="text-center mb-8 sm:mb-12">
              <h3 className="text-base sm:text-lg font-bold mb-4 sm:mb-6 text-white">Sales &amp; Service Offices</h3>
            </div>

            <div 
              ref={clientsHeader.ref}
              className="text-center mb-8 sm:mb-12"
              style={{
                opacity: clientsHeader.isVisible ? 1 : 0,
                transform: clientsHeader.isVisible ? 'translateY(0)' : 'translateY(40px)',
                transition: 'opacity 0.7s ease, transform 0.7s ease',
              }}
            >
              <h3 className="text-base sm:text-lg font-bold mb-4 sm:mb-6 text-white">Sales &amp; Service Offices</h3>
            </div>

            <div 
              ref={clientsGrid.ref}
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4 md:gap-6"
              style={{
                opacity: clientsGrid.isVisible ? 1 : 0,
                transform: clientsGrid.isVisible ? 'translateY(0)' : 'translateY(30px)',
                transition: 'opacity 0.8s ease, transform 0.8s ease',
              }}
            >
              {[
                { name: 'Legendary Motor Cars', image: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/9e6df3412adda2d95f0a644902d8400b.jpeg' },
                { name: 'Drake Hotel', image: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/6bba165c175149448f412e0b5a465dc8.jpeg' },
                { name: 'Blue Giant', image: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/02e7d437603f3d4f33a34900627a2e36.jpeg' },
                { name: 'Crane Worldwide', image: 'https://readdy.ai/api/search-image?query=Crane%20Worldwide%20logistics%20company%20corporate%20logo%20bold%20professional%20text%20on%20clean%20white%20background%20brand%20identity%20simple%20minimalist%20design%20high%20resolution&width=300&height=200&seq=client4&orientation=landscape' },
                { name: 'Subway', image: 'https://readdy.ai/api/search-image?query=Subway%20restaurant%20franchise%20corporate%20logo%20green%20yellow%20text%20on%20clean%20white%20background%20brand%20identity%20simple%20minimalist%20design%20high%20resolution&width=300&height=200&seq=client5&orientation=landscape' },
                { name: 'Cellular Point', image: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/2047f975488bf4bb29a6eb36a486fd52.jpeg' },
                { name: 'Hellenic Homes for the Aged', image: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/81919f4bef9936ad71d0604319899626.jpeg' },
                { name: 'Value Mobile', image: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/b95e42886136b018f9cc73705847419f.png' },
                { name: 'Drake Devonshire', image: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/63d4bbcb4a2dd9c4465d50455f20fdf5.png' },
                { name: 'Cargo Cabbie', image: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/2248d44db3d17800674d55e9c0795114.jpeg' },
                { name: 'Aramark', image: 'https://readdy.ai/api/search-image?query=Aramark%20food%20service%20company%20corporate%20logo%20professional%20text%20on%20clean%20white%20background%20brand%20identity%20simple%20minimalist%20design%20high%20resolution&width=300&height=200&seq=client11&orientation=landscape' },
                { name: 'ASUS', image: 'https://readdy.ai/api/search-image?query=ASUS%20computer%20technology%20corporate%20logo%20bold%20text%20on%20clean%20white%20background%20brand%20identity%20simple%20minimalist%20design%20high%20resolution&width=300&height=200&seq=client12&orientation=landscape' },
                { name: 'Bulk Barn', image: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/8132d25968fe644fe51090f332b4195c.png' },
                { name: 'Queensgate Homes', image: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/5df9838c13500b82f3f41db35bb26c67.png' },
                { name: 'Essdo', image: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/5379746381228b5f1ecce8f7df64d64c.jpeg' },
                { name: 'First National', image: 'https://readdy.ai/api/search-image?query=First%20National%20mortgage%20lender%20corporate%20logo%20professional%20text%20on%20clean%20white%20background%20brand%20identity%20simple%20minimalist%20design%20high%20resolution&width=300&height=200&seq=client16&orientation=landscape' },
                { name: 'Glen Rouge Homes', image: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/1398e60f073aa78cd7f6fe2a31ba6b52.png' },
                { name: 'Mississauga Hardware', image: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/72486fbd0d75e3b14e4848b702f09aa3.jpeg' },
                { name: 'The Humberview Group', image: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/b9711ad6a158fa60d5e032b0dbee5c96.png' },
                { name: 'Seacore Seafood', image: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/b95e42886136b018f9cc73705847419f.png' },
                { name: 'Coffee Time', image: 'https://readdy.ai/api/search-image?query=Coffee%20Time%20cafe%20franchise%20corporate%20logo%20warm%20text%20on%20clean%20white%20background%20brand%20identity%20simple%20minimalist%20design%20high%20resolution&width=300&height=200&seq=client21&orientation=landscape' },
                { name: 'NCCF', image: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/c2c704ac8d9b413ed86bec813a835585.png' },
                { name: 'All Crane', image: 'https://readdy.ai/api/search-image?query=All%20Crane%20rental%20equipment%20corporate%20logo%20industrial%20text%20on%20clean%20white%20background%20brand%20identity%20simple%20minimalist%20design%20high%20resolution&width=300&height=200&seq=client23&orientation=landscape' },
                { name: 'Maverick Distillery', image: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/b79edc79400a27859ee3eb45c3c656ee.png' },
                { name: 'Esso', image: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/62b4690d857f54c9ea33e651269be273.png' },
                { name: 'Tim Hortons', image: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/a3f9260e0ccd0866f43363c524edfe05.png' },
                { name: 'Shoppers Drug Mart', image: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/ff8f6ab6add3ac8f147a6768092ebd47.jpeg' },
                { name: 'Canadian Tire', image: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/e27adcf7cadbbcf092376d8388119615.jpeg' },
                { name: 'Petro-Canada', image: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/6fd00cda4eae2accbb5b98dca0c1a8c2.png' },
                { name: 'Royal LePage', image: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/9df6c49ff0bbf5958d438e347e334adb.png' },
                { name: 'Marriott Hotels', image: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/469dff91b229d0995c855e2a80feabff.png' },
                { name: 'Goodlife Fitness', image: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/69ed7aa3db7a11c53f836f7cd5216ede.png' },
                { name: 'Rexall Pharmacy', image: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/f258306d621c4527671e4a69c07cba91.png' },
                { name: 'Pizza Nova', image: 'https://readdy.ai/api/search-image?query=Pizza%20Nova%20restaurant%20franchise%20corporate%20logo%20red%20text%20on%20clean%20white%20background%20brand%20identity%20simple%20minimalist%20design%20high%20resolution&width=300&height=200&seq=client34&orientation=landscape' },
                { name: 'Cineplex', image: 'https://readdy.ai/api/search-image?query=Cineplex%20movie%20theater%20chain%20corporate%20logo%20red%20text%20on%20clean%20white%20background%20brand%20identity%20simple%20minimalist%20design%20high%20resolution&width=300&height=200&seq=client35&orientation=landscape' },
                { name: 'LCBO', image: 'https://static.readdy.ai/image/fff1181c13d1d5fdcfabeab4ada583e3/3a6547bcacca34d1321b942af4e3948a.png' }
              ].map((client, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-xl p-3 sm:p-4 md:p-6 shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center h-20 sm:h-24 md:h-28 border border-gray-100 hover:border-[#D4A853]/30"
                >
                  <img src={client.image} alt={client.name} title={client.name} className="max-w-full max-h-full object-contain grayscale hover:grayscale-0 transition-all duration-300 scale-110 sm:scale-125" />
                </div>
              ))}
            </div>

            <div className="mt-8 sm:mt-12 text-center">
              <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">Join hundreds of businesses that trust us with their security</p>
              <button 
                onClick={() => scrollToSection('contact')}
                className="bg-[#D4A853] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap"
              >
                Become a Client
              </button>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="relative py-12 sm:py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src="https://readdy.ai/api/search-image?query=blurred%20modern%20security%20operations%20center%20with%20monitors%20and%20equipment%20dark%20blue%20ambient%20lighting%20professional%20technology%20environment%20out%20of%20focus%20background&width=1920&height=1080&seq=contact1&orientation=landscape" 
              alt="Contact Background"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b sm:bg-gradient-to-r from-[#0A1628]/95 via-[#0A1628]/90 to-[#0A1628]/80 sm:to-transparent"></div>
          </div>

          <div 
            ref={contactSection.ref}
            className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6"
            style={{
              opacity: contactSection.isVisible ? 1 : 0,
              transform: contactSection.isVisible ? 'translateY(0)' : 'translateY(50px)',
              transition: 'opacity 0.8s ease, transform 0.8s ease',
            }}
          >
            <div className="max-w-2xl">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-8">Ready to Secure Your Property?</h2>
              <p className="text-base sm:text-lg md:text-xl text-white/80 mb-6 sm:mb-8">Speak with a security expert today</p>
              <p className="text-sm sm:text-base md:text-lg text-white/60 mb-8 sm:mb-12">Response time: Under 2 hours</p>
              
              <div className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-7 md:p-10 shadow-2xl">
                {showContactSuccess && (
                  <div className="mb-6 bg-green-50 border border-green-200 text-green-800 px-4 sm:px-6 py-3 sm:py-4 rounded-xl">
                    <p className="font-semibold text-sm sm:text-base">Thank you for your inquiry!</p>
                    <p className="text-xs sm:text-sm">We'll contact you within 2 hours.</p>
                  </div>
                )}
                
                <form className="space-y-4 sm:space-y-6" data-readdy-form id="contact-form" onSubmit={handleContactSubmit}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">First Name</label>
                      <input type="text" name="first_name" required className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D4A853] text-sm" placeholder="John" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name</label>
                      <input type="text" name="last_name" required className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D4A853] text-sm" placeholder="Doe" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address</label>
                    <input type="email" name="email" required className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D4A853] text-sm" placeholder="john@example.com" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number</label>
                    <input type="tel" name="phone" required className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D4A853] text-sm" placeholder="(555) 123-4567" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Property Type</label>
                    <select name="property_type" required className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D4A853] text-sm cursor-pointer">
                      <option value="">Select property type</option>
                      <option value="residential">Residential</option>
                      <option value="commercial">Commercial</option>
                      <option value="industrial">Industrial</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Services Interested In</label>
                    <div className="space-y-3">
                      {['Alarm Systems', 'Security Cameras', 'Card Access Control', 'Live Video Monitoring'].map((service) => (
                        <label key={service} className="flex items-center gap-3 cursor-pointer">
                          <input type="checkbox" name="services" value={service} className="w-5 h-5 text-[#D4A853] border-gray-300 rounded focus:ring-2 focus:ring-[#D4A853] cursor-pointer" />
                          <span className="text-sm text-gray-700">{service}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Additional Details</label>
                    <textarea name="message" rows={4} maxLength={500} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D4A853] text-sm resize-none" placeholder="Tell us about your security needs..."></textarea>
                    <p className="text-xs text-gray-500 mt-2">Maximum 500 characters</p>
                  </div>
                  <button type="submit" className="w-full bg-[#D4A853] text-white py-3.5 sm:py-4 rounded-full text-sm sm:text-lg font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap shadow-lg">
                    Schedule Free Consultation
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Service Areas Section */}
        <section id="service-areas" className="py-12 sm:py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
              {[
                { city: 'Mississauga', region: 'Peel Region' }, { city: 'Brampton', region: 'Peel Region' },
                { city: 'Toronto', region: 'Toronto' }, { city: 'Hamilton', region: 'Hamilton' },
                { city: 'Oakville', region: 'Halton Region' }, { city: 'Milton', region: 'Halton Region' },
                { city: 'Burlington', region: 'Halton Region' }, { city: 'Woodbridge', region: 'York Region' },
                { city: 'King City', region: 'York Region' }, { city: 'Stouffville', region: 'York Region' },
                { city: 'Richmond Hill', region: 'York Region' }, { city: 'Maple', region: 'York Region' },
                { city: 'Newmarket', region: 'York Region' }, { city: 'Aurora', region: 'York Region' },
                { city: 'New Tecumseth', region: 'Simcoe County' }, { city: 'Whitby', region: 'Durham Region' },
                { city: 'Ajax', region: 'Durham Region' }, { city: 'Oshawa', region: 'Durham Region' },
                { city: 'Ancaster', region: 'Hamilton' }, { city: 'Stoney Creek', region: 'Hamilton' }
              ].map((area, index) => (
                <div 
                  key={index}
                  className="bg-[#F8F9FA] rounded-xl p-3 sm:p-4 md:p-5 hover:bg-[#D4A853]/10 hover:border-[#D4A853]/30 border border-gray-100 transition-all duration-300 cursor-pointer group"
                >
                  <div className="flex items-start gap-2 sm:gap-3">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-[#D4A853]/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-2-fill text-[#D4A853] group-hover:text-white text-xs sm:text-sm"></i>
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-bold text-[#0A1628] text-sm sm:text-base mb-0.5 sm:mb-2 truncate">{area.city}</h3>
                      <p className="text-[10px] sm:text-xs text-gray-500 truncate">{area.region}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div 
              ref={serviceAreasCta.ref}
              className="mt-8 sm:mt-12 bg-gradient-to-r from-[#0A1628] to-[#1a2d4a] rounded-2xl p-6 sm:p-8 md:p-12"
              style={{
                opacity: serviceAreasCta.isVisible ? 1 : 0,
                transform: serviceAreasCta.isVisible ? 'translateY(0)' : 'translateY(30px)',
                transition: 'opacity 0.7s ease 0.2s, transform 0.7s ease 0.2s',
              }}
            >
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-6">
                <div className="text-center sm:text-left">
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2">Don't See Your Area?</h3>
                  <p className="text-sm sm:text-base text-white/70">We may still be able to help! Contact us to discuss your location.</p>
                </div>
                <button 
                  onClick={() => scrollToSection('contact')}
                  className="bg-[#D4A853] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-phone-line"></i>
                  Contact Us Today
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer 
          ref={footerSection.ref}
          className="bg-[#050B14] text-white pt-12 sm:pt-16 md:pt-20 pb-8 sm:pb-12 mx-3 sm:mx-6 rounded-t-[24px] sm:rounded-t-[32px] relative overflow-hidden"
          style={{
            opacity: footerSection.isVisible ? 1 : 0,
            transform: footerSection.isVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}
        >
          <div className="absolute inset-0 text-[60px] sm:text-[80px] md:text-[120px] font-extrabold text-white/5 text-center leading-none pb-4">
            SECURITY
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 md:gap-12 mb-8 sm:mb-12">
              <div>
                <div className="inline-block border-2 border-[#D4A853] rounded-lg px-4 py-2.5 bg-[#0A1628] mb-4">
                  <div className="flex flex-col leading-none">
                    <span className="text-xl sm:text-2xl font-extrabold tracking-wider text-[#D4A853]" style={{ fontFamily: "'Inter', sans-serif" }}>ALLIANCE</span>
                    <span className="text-[8px] sm:text-xs font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
                  </div>
                </div>
                <p className="text-white/60 mb-4 sm:mb-6 text-sm">Trusted Security Since 1999</p>
                <a 
                  href="tel:18884589181"
                  className="inline-block bg-[#D4A853] text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-full text-xs sm:text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-phone-line mr-2"></i>
                  Emergency: 1-888-458-9181
                </a>
              </div>

              <div>
                <h3 className="text-base sm:text-lg font-bold mb-4 sm:mb-6 text-white">Head Office</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-3">
                    <i className="ri-map-pin-line text-[#D4A853] text-lg flex-shrink-0 mt-0.5"></i>
                    <div>
                      <p className="text-white/80 leading-relaxed">2355 Derry Road East, Unit 1</p>
                      <p className="text-white/80">Mississauga, ON L5S 1V6</p>
                    </div>
                  </div>
                  <a href="tel:9056960613" className="flex items-center gap-3 text-white/80 hover:text-[#D4A853] transition-colors cursor-pointer">
                    <i className="ri-phone-line text-[#D4A853] text-lg"></i>
                    (905) 696-0613
                  </a>
                  <a href="tel:18884589181" className="flex items-center gap-3 text-white/80 hover:text-[#D4A853] transition-colors cursor-pointer">
                    <i className="ri-phone-line text-[#D4A853] text-lg"></i>
                    1-888-458-9181
                  </a>
                </div>
              </div>

              <div>
                <h3 className="text-base sm:text-lg font-bold mb-4 sm:mb-6 text-white">Head Office</h3>
                <div className="space-y-4 text-sm">
                  <div>
                    <p className="text-[#D4A853] font-semibold mb-2">Hamilton Office</p>
                    <div className="flex items-start gap-3 mb-2">
                      <i className="ri-map-pin-line text-white/60 text-base flex-shrink-0 mt-0.5"></i>
                      <div>
                        <p className="text-white/80 leading-relaxed">1 Hunter Street East, Ground Floor</p>
                        <p className="text-white/80">Hamilton, ON L8N 3W1</p>
                      </div>
                    </div>
                    <a href="tel:18884589181" className="flex items-center gap-2 text-white/80 hover:text-[#D4A853] transition-colors cursor-pointer">
                      <i className="ri-phone-line text-white/60 text-base"></i>
                      1-888-458-9181
                    </a>
                  </div>

                  <div>
                    <p className="text-[#D4A853] font-semibold mb-2">Whitby Office</p>
                    <div className="flex items-start gap-3 mb-2">
                      <i className="ri-map-pin-line text-white/60 text-base flex-shrink-0 mt-0.5"></i>
                      <div>
                        <p className="text-white/80 leading-relaxed">Durham Corporate Centre</p>
                        <p className="text-white/80">Whitby, ON L1N 1C4</p>
                      </div>
                    </div>
                    <a href="tel:18884589181" className="flex items-center gap-2 text-white/80 hover:text-[#D4A853] transition-colors cursor-pointer">
                      <i className="ri-phone-line text-white/60 text-base"></i>
                      1-888-458-9181
                    </a>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-base sm:text-lg font-bold mb-4 sm:mb-6 text-white">Get Security Tips</h3>
                <p className="text-[#9CA3AF] text-xs sm:text-sm mb-3 sm:mb-4">Monthly security insights delivered to your inbox</p>
                {showNewsletterSuccess && (
                  <div className="mb-4 bg-green-500/20 border border-green-500/50 text-green-300 px-4 py-2 rounded-lg text-xs">Successfully subscribed!</div>
                )}
                <form className="flex gap-2 mb-4 sm:mb-6" data-readdy-form id="newsletter-form" onSubmit={handleNewsletterSubmit}>
                  <input type="email" name="email" required placeholder="Your email" className="flex-1 min-w-0 px-3 sm:px-4 py-2.5 sm:py-3 bg-white/10 border border-white/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D4A853] text-xs sm:text-sm text-white placeholder-white/50" />
                  <button type="submit" className="w-10 h-10 sm:w-12 sm:h-12 bg-[#D4A853] rounded-full flex items-center justify-center hover:bg-[#B8923D] transition-all cursor-pointer flex-shrink-0">
                    <i className="ri-arrow-right-line text-lg sm:text-xl text-white"></i>
                  </button>
                </form>
                <div className="space-y-1.5 text-sm">
                  <Link to="/blog" className="block text-white/60 hover:text-[#D4A853] transition-colors cursor-pointer">Blog</Link>
                  <Link to="/faq" className="block text-white/60 hover:text-[#D4A853] transition-colors cursor-pointer">FAQ</Link>
                  <Link to="/service-areas" className="block text-white/60 hover:text-[#D4A853] transition-colors cursor-pointer">Service Areas</Link>
                  <Link to="/contact" className="block text-white/60 hover:text-[#D4A853] transition-colors cursor-pointer">Contact Us</Link>
                </div>
              </div>
            </div>

            {/* Google Business Profile & Review Badges */}
            <div className="mb-8 sm:mb-12 pt-8 sm:pt-10 border-t border-white/10">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-8">
                  <div className="flex items-center gap-6 sm:gap-8">
                    {/* BBB Badge */}
                    <div className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-5 py-3.5">
                      <div className="w-10 h-10 bg-[#003DA5] rounded-lg flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-extrabold text-lg leading-none">A+</span>
                      </div>
                      <div>
                        <p className="text-white/80 text-xs font-semibold">BBB Accredited</p>
                        <p className="text-white/50 text-[11px]">A+ Rating Since 1999</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-4 py-3">
                    <i className="ri-shield-check-line text-[#D4A853] text-lg"></i>
                    <span className="text-white/70 text-xs font-semibold whitespace-nowrap">30,000+ Installations</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-4 py-3">
                    <i className="ri-time-line text-[#D4A853] text-lg"></i>
                    <span className="text-white/70 text-xs font-semibold whitespace-nowrap">25+ Years Trusted</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-6 sm:pt-8 border-t border-white/10 flex flex-col items-center gap-6 sm:gap-8">
              <div className="text-center sm:text-left">
                <p className="text-[#9CA3AF] text-xs sm:text-sm">© 2024 Security Solutions. All rights reserved.</p>
                <div className="flex flex-wrap gap-3">
                  <Link to="/privacy-policy" className="text-[#9CA3AF] hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">Privacy Policy</Link>
                  <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-[#9CA3AF] hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">Powered by Readdy</a>
                </div>
              </div>
            </div>
          </div>
        </footer>

        {/* Back to Top Button */}
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 left-6 z-50 w-12 h-12 bg-[#0A1628] text-white rounded-full flex items-center justify-center shadow-lg hover:bg-[#D4A853] transition-all duration-300 cursor-pointer"
          style={{
            opacity: showBackToTop ? 1 : 0,
            transform: showBackToTop ? 'translateY(0)' : 'translateY(20px)',
            pointerEvents: showBackToTop ? 'auto' : 'none',
            transition: 'opacity 0.3s ease, transform 0.3s ease, background-color 0.3s ease',
          }}
          aria-label="Back to top"
        >
          <i className="ri-arrow-up-line text-xl"></i>
        </button>

        <EngagementBar />
      </main>
    </div>
  );
}
